import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Modal, Button, Input } from '@core/components/ui';
import { validateFolderName } from '../utils/folderValidation';

/**
 * CreateFolderModal Component
 *
 * Modal dialog for creating new folders.
 * Follows Google Drive / Dropbox UX patterns.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onCreate - Callback to create folder (receives folder name)
 * @param {boolean} [props.isCreating=false] - Whether creation is in progress
 */
const CreateFolderModal = ({ isOpen, onClose, onCreate, isCreating = false }) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  // Handle create
  const handleCreate = useCallback(async () => {
    const validationError = validateFolderName(folderName, t);

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onCreate(folderName.trim());
      // On success, reset and close
      setFolderName('');
      setError('');
    } catch (err) {
      // Handle RTK Query errors and other errors
      const errorMessage = err?.data?.error || err?.message || t('files.folders.createError', 'Error al crear la carpeta');
      setError(errorMessage);
    }
  }, [folderName, onCreate, validateFolderName, t]);

  // Handle close
  const handleClose = useCallback(() => {
    setFolderName('');
    setError('');
    onClose();
  }, [onClose]);

  // Handle key down
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !isCreating) {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  }, [handleCreate, handleClose, isCreating]);

  // Handle input change
  const handleInputChange = (value) => {
    setFolderName(value);
    // Clear error when user starts typing
    setError('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('files.explorer.createFolderTitle', 'Crear nueva carpeta')}
      size="sm"
      closeButton
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isCreating}
            className="cursor-pointer"
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={isCreating || !folderName.trim()}
            className="cursor-pointer"
          >
            {isCreating ? (
              <>
                <Icon icon="eos-icons:loading" className="w-5 h-5 mr-2" />
                {t('common.creating', 'Creando...')}
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="w-5 h-5 mr-2" />
                {t('files.explorer.create', 'Crear')}
              </>
            )}
          </Button>
        </>
      }
    >
      <Input
        label={t('files.explorer.folderNameLabel', 'Nombre de la carpeta')}
        value={folderName}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        error={error}
        disabled={isCreating}
        autoFocus
      />
    </Modal>
  );
};

CreateFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isCreating: PropTypes.bool,
};

export { CreateFolderModal };
